import BlogApi from 'api/BlogApi'
import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { POSTS_PER_PAGE } from './constants'
import CategoryFilters from './CategoryFilters'

import { ContentContainer, PostsContainer, SidebarContainer } from './styled'
import CategoryFilterLoadingState from './LoadingState/CategoryFilterLoadingState'
import ContentLoadingState from './LoadingState/ContentLoadingState'
import BlogSidebar from './BlogSidebar'
import Posts from './Posts'
import { useRouter } from 'next/router'

const Blog = () => {
  const router = useRouter()

  const [categoryId, setCategoryId] = useState<number | undefined>()
  const [tagIds, setTagIds] = useState<number[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [initializedFromQuery, setInitializedFromQuery] = useState(false)

  useEffect(() => {
    if (!router.isReady || initializedFromQuery) return

    const { category, tags, search, ...restQuery } = router.query as Record<string, any>
    const cleanedQuery: Record<string, string> = { ...restQuery }

    if (category && Number.isInteger(Number(category))) {
      setCategoryId(Number(category))
      cleanedQuery.category = String(category)
    } else {
      delete cleanedQuery.category
    }

    if (tags) {
      const tagList = Array.isArray(tags) ? tags.flatMap((tag) => tag.split(',')) : tags.split(',')

      const validTags = tagList
        .map((id: string) => Number(id))
        .filter((id: number) => Number.isInteger(id))

      if (validTags.length > 0) {
        setTagIds(validTags)
        cleanedQuery.tags = validTags.join(',')
      } else {
        delete cleanedQuery.tags
      }
    }

    if (search) {
      setSearchTerm(search)
      cleanedQuery.search = search
    } else {
      delete cleanedQuery.search
    }

    const original = new URLSearchParams(router.query as any).toString()
    const cleaned = new URLSearchParams(cleanedQuery).toString()

    if (original !== cleaned) {
      router.replace(
        {
          pathname: router.pathname,
          query: cleanedQuery,
        },
        undefined,
        { shallow: true },
      )
    }
    setInitializedFromQuery(true)
  }, [router.isReady, initializedFromQuery])

  useEffect(() => {
    setPage(1)
  }, [categoryId, tagIds, searchTerm])

  const { data: posts, isLoading: isLoadingPosts } = useQuery(
    ['posts', categoryId, tagIds, searchTerm, page],
    () =>
      BlogApi.getPosts({
        category: categoryId,
        tags: tagIds.length ? tagIds.join(',') : undefined,
        search: searchTerm,
        page,
      }),
    {
      enabled: initializedFromQuery,
    },
  )

  const { data: mostRead, isLoading: isLoadingMostRead } = useQuery(
    ['blogs-most-read'],
    BlogApi.getMostRead,
  )

  const { data: filters, isLoading: isLoadingFilters } = useQuery(
    ['blogs-filters'],
    BlogApi.getTagsAndCategory,
  )

  const totalPages = Math.ceil((posts?.count || 0) / POSTS_PER_PAGE)

  return (
    <>
      {isLoadingFilters ? (
        <CategoryFilterLoadingState />
      ) : (
        <CategoryFilters
          categories={filters}
          setCategoryId={setCategoryId}
          categoryId={categoryId}
        />
      )}
      <ContentContainer>
        <PostsContainer>
          {isLoadingPosts ? (
            <ContentLoadingState />
          ) : (
            <Posts posts={posts} page={page} setPage={setPage} totalPages={totalPages} />
          )}
        </PostsContainer>
        <SidebarContainer>
          {isLoadingMostRead || isLoadingFilters ? (
            <ContentLoadingState />
          ) : (
            <BlogSidebar
              mostRead={mostRead}
              filters={filters}
              tagIds={tagIds}
              setTagIds={setTagIds}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          )}
        </SidebarContainer>
      </ContentContainer>
    </>
  )
}

export default Blog
